import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  CheckCircle2, 
  Check, 
  Loader2,
  Menu,
  AlertCircle,
  RefreshCw,
  Home,
  Building2,
  Users,
  Briefcase,
  CreditCard,
  Building,
  FileCheck,
  Target,
  TrendingUp,
  Sparkles,
  User,
  GraduationCap,
  BookOpen,
  Award,
  Car,
  Store,
  Wallet,
  DollarSign,
  Percent,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  Shield,
  LucideIcon,
} from "lucide-react";
import { Lead, LoanType } from "@/types";
import {
  getQuestionnaireForLoanType,
  QuestionnaireSection as StaticQuestionnaireSection,
  validateField,
} from "@/config/questionnaireConfig";
import { QuestionnaireFormField } from "./QuestionnaireFormField";
import { toast } from "sonner";
import { updateLead } from "@/services/leadService";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuestionnaireForm } from "@/hooks/useQuestionnaireForm";
import type { TemplateSection, TemplateField, ConditionalLogic } from "@/types/questionnaire";
import { useModal } from "@/contexts/ModalContext";

// Icon mapping from iconName string to actual icon component
const ICON_MAP: Record<string, LucideIcon> = {
  Home,
  Building2,
  Users,
  Briefcase,
  CreditCard,
  Building,
  FileCheck,
  Target,
  TrendingUp,
  Sparkles,
  User,
  GraduationCap,
  BookOpen,
  Award,
  Car,
  Store,
  Wallet,
  DollarSign,
  Percent,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  Shield,
  CheckCircle: CheckCircle2,
};

// Get icon component from iconName string
const getIcon = (iconName?: string): LucideIcon => {
  if (!iconName) return FileCheck;
  return ICON_MAP[iconName] || FileCheck;
};

// Unified section type that works with both static and Firebase data
interface QuestionnaireSection {
  id: string;
  title: string;
  description?: string;
  icon?: LucideIcon;
  iconName?: string;
  fields: Array<{
    id: string;
    type: string;
    label: string;
    placeholder?: string;
    options?: { value: string; label: string }[];
    validation?: {
      required?: boolean;
      min?: number;
      max?: number;
      pattern?: string;
      minLength?: number;
      maxLength?: number;
    };
    conditional?: (data: any) => boolean;
    conditionalLogic?: ConditionalLogic;
  }>;
  conditional?: (data: any) => boolean;
  conditionalLogic?: ConditionalLogic;
}

interface DynamicQuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  onSave: (updatedLead: Lead) => void;
}

// Evaluate conditional logic from Firebase
const evaluateConditionalLogic = (
  conditionalLogic: ConditionalLogic | undefined,
  data: Record<string, any>
): boolean => {
  if (!conditionalLogic) return true;
  
  const { action, rules, logicType } = conditionalLogic;
  
  const evaluateRule = (rule: ConditionalLogic['rules'][0]): boolean => {
    const fieldValue = data[rule.fieldId];
    
    switch (rule.operator) {
      case 'equals':
        return fieldValue === rule.value;
      case 'not_equals':
        return fieldValue !== rule.value;
      case 'contains':
        return typeof fieldValue === 'string' && fieldValue.includes(rule.value as string);
      case 'not_contains':
        return typeof fieldValue === 'string' && !fieldValue.includes(rule.value as string);
      case 'greater_than':
        return Number(fieldValue) > Number(rule.value);
      case 'less_than':
        return Number(fieldValue) < Number(rule.value);
      case 'in':
        return Array.isArray(rule.value) && rule.value.includes(fieldValue);
      case 'not_in':
        return Array.isArray(rule.value) && !rule.value.includes(fieldValue);
      case 'is_empty':
        return fieldValue === undefined || fieldValue === null || fieldValue === '';
      case 'is_not_empty':
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
      default:
        return true;
    }
  };
  
  let result: boolean;
  if (logicType === 'and') {
    result = rules.every(evaluateRule);
  } else {
    result = rules.some(evaluateRule);
  }
  
  // For 'show' action, return result directly
  // For 'hide' action, return opposite
  if (action === 'hide') {
    return !result;
  }
  
  return result;
};

// Convert Firebase TemplateSection to unified QuestionnaireSection
const convertTemplateSection = (section: TemplateSection): QuestionnaireSection => ({
  id: section.id,
  title: section.title,
  description: section.description,
  icon: getIcon(section.iconName),
  iconName: section.iconName,
  fields: section.fields.map(field => ({
    id: field.id,
    type: field.type,
    label: field.label,
    placeholder: field.placeholder,
    options: field.options,
    validation: field.validation,
    conditionalLogic: field.conditionalLogic,
  })),
  conditionalLogic: section.conditionalLogic,
});

export function DynamicQuestionnaireDialog({
  open,
  onOpenChange,
  lead,
  onSave,
}: DynamicQuestionnaireDialogProps) {
  const isMobile = useIsMobile();
  const { registerModal, unregisterModal } = useModal();
  useEffect(() => {
    if (!open) return;
    registerModal('lead-questionnaire-dialog', () => onOpenChange(false), 1);
    return () => unregisterModal('lead-questionnaire-dialog');
  }, [open, onOpenChange, registerModal, unregisterModal]);

  // Get lenderId from lead if available
  const lenderId = (lead as any).lenderId || (lead as any).selectedLender;
  
  // Fetch form from Firebase with fallback to static
  // Pass leadId for version-aware fetching (uses locked version if lead has existing response)
  const { 
    form: firebaseForm, 
    isLoading: isFormLoading, 
    error: formError,
    isFromFallback,
    isVersionLocked,
    existingResponse,
    refresh: refreshForm
  } = useQuestionnaireForm(lead.loanType as LoanType, lenderId, { 
    skip: !open,
    leadId: lead.id // Pass leadId for version-aware fetching
  });
  
  // Fallback to static config
  const staticQuestionnaire = getQuestionnaireForLoanType(lead.loanType as LoanType);
  
  // Prepare sections - use Firebase form or fall back to static
  const sections: QuestionnaireSection[] = React.useMemo(() => {
    if (firebaseForm && firebaseForm.sections.length > 0) {
      return firebaseForm.sections.map(convertTemplateSection);
    }
    // Fallback to static config
    return staticQuestionnaire?.sections?.map(section => ({
      ...section,
      icon: section.icon,
    })) || [];
  }, [firebaseForm, staticQuestionnaire]);
  
  const loanTypeName = firebaseForm?.name || (lead.loanType ? lead.loanType.replace(/_/g, ' ').toUpperCase() : 'LOAN');
  
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showSectionMenu, setShowSectionMenu] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // Load data when dialog opens
  useEffect(() => {
    if (open && !isInitializedRef.current) {
      if (lead.questionnaireData) {
        const existingData = lead.questionnaireData as Record<string, any>;
        setFormData(existingData);
        setCompletedSections(existingData.completedSections || []);
        if (existingData.lastUpdated) {
          const date = existingData.lastUpdated instanceof Date 
            ? existingData.lastUpdated 
            : new Date(existingData.lastUpdated);
          setLastSaved(date);
        }
      } else {
        setFormData({});
        setCompletedSections([]);
        setLastSaved(null);
      }
      isInitializedRef.current = true;
    }
    
    // Reset initialization flag when dialog closes
    if (!open) {
      isInitializedRef.current = false;
    }
  }, [open, lead.questionnaireData]);

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Guard against empty sections
  if (sections.length === 0 && !isFormLoading) {
    return null;
  }

  const currentSection = sections[currentSectionIndex] || sections[0];
  const totalSections = sections.length;
  const progress = totalSections > 0 ? ((currentSectionIndex + 1) / totalSections) * 100 : 0;
  
  // Get icon for section (handle both icon component and iconName string)
  const getSectionIcon = (section: QuestionnaireSection): LucideIcon => {
    if (section.icon) return section.icon;
    if (section.iconName) return getIcon(section.iconName);
    return FileCheck;
  };
  
  // Safe conditional check helper - handles both function-based and Firebase conditionalLogic
  const safeConditional = (field: { conditional?: (data: any) => boolean; conditionalLogic?: ConditionalLogic }, data: Record<string, any>): boolean => {
    // First try function-based conditional (static config)
    if (field.conditional) {
      try {
        return field.conditional(data);
      } catch (e) {
        console.warn('Conditional evaluation error:', e);
        return false;
      }
    }
    // Then try Firebase conditionalLogic
    if (field.conditionalLogic) {
      return evaluateConditionalLogic(field.conditionalLogic, data);
    }
    return true;
  };
  
  // Check if section is complete
  const isSectionComplete = (section: QuestionnaireSection, data: Record<string, any>): boolean => {
    return section.fields.every((field) => {
      // Skip conditional fields that are hidden
      if (!safeConditional(field, data)) {
        return true;
      }
      // Check required fields
      if (field.validation?.required) {
        const value = data[field.id];
        return value !== undefined && value !== null && value !== '';
      }
      return true;
    });
  };

  // Handle save function
  const handleSave = useCallback(async (final: boolean = false, dataToSave?: Record<string, any>) => {
    const saveData = dataToSave || formData;
    
    // Check if there's actual data to save (beyond metadata)
    const hasRealData = Object.keys(saveData).some(key => 
      !['loanType', 'completedSections', 'lastUpdated'].includes(key) &&
      saveData[key] !== undefined && 
      saveData[key] !== null && 
      saveData[key] !== ''
    );
    
    // Don't auto-save if there's no real data
    if (!hasRealData && !final) {
      return;
    }
    
    if (final) {
      // Validate current section for final save
      const sectionErrors: Record<string, string> = {};
      let isValid = true;

      currentSection.fields.forEach((field) => {
        if (!safeConditional(field, saveData)) {
          return;
        }
        const error = validateField(field, saveData[field.id]);
        if (error) {
          sectionErrors[field.id] = error;
          isValid = false;
        }
      });

      if (!isValid) {
        setErrors(sectionErrors);
        toast.error("Please fill all required fields correctly");
        return;
      }
    }

    setIsSaving(true);
    try {
      const updatedQuestionnaireData = {
        ...saveData,
        loanType: lead.loanType,
        completedSections: final
          ? [...new Set([...completedSections, currentSection.id])]
          : completedSections,
        lastUpdated: new Date(),
      };

      const updatedLead: Lead = {
        ...lead,
        questionnaireData: updatedQuestionnaireData,
      };

      await updateLead(lead.id!, updatedLead);
      onSave(updatedLead);
      
      // Update last saved timestamp
      setLastSaved(new Date());

      if (final) {
        toast.success("Questionnaire submitted successfully!");
        onOpenChange(false);
      } else {
        // Silent save for auto-save (no toast)
        if (!dataToSave) {
          toast.success("Progress saved successfully");
        }
      }
    } catch (error) {
      console.error("Error saving questionnaire:", error);
      toast.error("Failed to save questionnaire");
    } finally {
      setIsSaving(false);
    }
  }, [formData, completedSections, currentSection, lead, onSave, onOpenChange]);
  
  // Debounced auto-save function
  const debouncedAutoSave = useCallback((data: Record<string, any>) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      handleSave(false, data);
    }, 3000); // Auto-save after 3 seconds of inactivity
  }, [handleSave]);

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prevData => {
      const newFormData = {
        ...prevData,
        [fieldId]: value,
      };
      
      // Trigger debounced auto-save with new data
      debouncedAutoSave(newFormData);
      
      return newFormData;
    });
    
    // Clear error for this field when user types
    if (errors[fieldId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const validateSection = (section: QuestionnaireSection): boolean => {
    const sectionErrors: Record<string, string> = {};
    let isValid = true;

    section.fields.forEach((field) => {
      // Check if field should be shown based on conditional logic
      if (!safeConditional(field, formData)) {
        return; // Skip validation for hidden fields
      }

      const error = validateField(field, formData[field.id]);
      if (error) {
        sectionErrors[field.id] = error;
        isValid = false;
      }
    });

    setErrors(sectionErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateSection(currentSection)) {
      // Mark section as completed
      if (!completedSections.includes(currentSection.id)) {
        setCompletedSections((prev) => [...prev, currentSection.id]);
      }

      if (currentSectionIndex < totalSections - 1) {
        const nextIndex = currentSectionIndex + 1;
        setCurrentSectionIndex(nextIndex);
        // Clear errors when moving to next section
        setErrors({});
      }
    } else {
      toast.error("Please fill all required fields correctly");
    }
  };

  const handlePrevious = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      setErrors({});
    }
  };

  const handleSubmit = () => {
    // Validate ALL sections before final submission
    let allValid = true;
    const allErrors: Record<string, string> = {};
    
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        // Check if field should be shown based on conditional logic
        if (!safeConditional(field, formData)) {
          return;
        }

        const error = validateField(field as any, formData[field.id]);
        if (error) {
          allErrors[field.id] = error;
          allValid = false;
        }
      });
    });

    if (!allValid) {
      setErrors(allErrors);
      toast.error("Please complete all required fields in all sections");
      return;
    }

    handleSave(true);
  };

  const renderField = (field: any) => {
    // Check if field should be shown based on conditional logic
    if (!safeConditional(field, formData)) {
      return null;
    }

    return (
      <QuestionnaireFormField
        key={field.id}
        field={field}
        value={formData[field.id]}
        onChange={(value) => handleFieldChange(field.id, value)}
        error={errors[field.id]}
      />
    );
  };
  
  // Get visible fields for current section
  const visibleFields = currentSection.fields.filter(
    (field) => safeConditional(field, formData)
  );
  
  // Render section header card
  const renderSectionHeader = () => (
    <Card className="border-l-4 border-l-primary">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            {React.createElement(getSectionIcon(currentSection), { 
              className: "h-5 w-5 text-primary" 
            })}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base">{currentSection.title}</h3>
            {currentSection.description && (
              <p className="text-xs text-muted-foreground mt-1">
                {currentSection.description}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
  
  // Render form fields
  const renderFormFields = (gridLayout: boolean = false) => (
    <div className={cn(
      "space-y-5",
      gridLayout && "lg:grid lg:grid-cols-2 lg:gap-x-6 lg:gap-y-5 lg:space-y-0"
    )}>
      {visibleFields.map((field) => (
        <div 
          key={field.id}
          className={cn(
            field.type === 'textarea' && gridLayout && "lg:col-span-2"
          )}
        >
          <QuestionnaireFormField
            field={field}
            value={formData[field.id]}
            onChange={(value) => handleFieldChange(field.id, value)}
            error={errors[field.id]}
          />
        </div>
      ))}
      
      {/* Empty State */}
      {visibleFields.length === 0 && (
        <Card className="border-dashed lg:col-span-2">
          <CardContent className="p-8 text-center">
            <Check className="h-12 w-12 mx-auto text-green-600 mb-3" />
            <p className="text-sm text-muted-foreground">
              No additional information required for this section
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
  
  // Render auto-save indicator
  const renderAutoSaveIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-3">
      {isSaving ? (
        <>
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Saving...</span>
        </>
      ) : lastSaved ? (
        <>
          <Check className="h-3 w-3 text-green-600" />
          <span className="text-xs text-muted-foreground">
            Saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
          </span>
        </>
      ) : null}
    </div>
  );

  return (
    <>
      {/* Loading State */}
      {isFormLoading && (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-md">
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading questionnaire...</p>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Error State */}
      {formError && !isFormLoading && sections.length === 0 && (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-md">
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-muted-foreground text-center">{formError}</p>
              <Button variant="outline" onClick={() => refreshForm()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Main Content - only show when not loading and sections exist */}
      {!isFormLoading && sections.length > 0 && (
        <>
      {/* Conditional Rendering: Mobile Sheet or Desktop Dialog */}
      {isMobile ? (
        // MOBILE: Bottom Sheet
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent 
            side="bottom" 
            className="h-[90vh] rounded-t-3xl p-0 flex flex-col overflow-y-auto"
          >
            {/* Mobile Header */}
            <SheetHeader className="px-4 py-4 border-b sticky top-0 bg-background/95 backdrop-blur-sm z-20">
              <div className="flex items-center justify-between mb-2">
                <SheetTitle className="text-lg">
                  {loanTypeName} Questionnaire
                </SheetTitle>
                <Badge variant="secondary" className="text-xs font-medium">
                  {currentSectionIndex + 1} / {totalSections}
                </Badge>
              </div>
              
              {/* Dot Indicators */}
              <div className="flex items-center justify-center gap-2 mb-3">
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    onClick={() => setCurrentSectionIndex(index)}
                    className={cn(
                      "transition-all rounded-full",
                      currentSectionIndex === index
                        ? "h-2.5 w-8 bg-primary"
                        : isSectionComplete(section, formData)
                        ? "h-2 w-2 bg-green-500"
                        : "h-2 w-2 bg-border"
                    )}
                    aria-label={`Go to ${section.title}`}
                  />
                ))}
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    {React.createElement(getSectionIcon(currentSection), { 
                      className: "h-4 w-4 text-primary" 
                    })}
                    <span className="text-sm font-medium">
                      {currentSection.title}
                    </span>
                  </div>
                  {isSectionComplete(currentSection, formData) && (
                    <Badge variant="default" className="bg-green-600 text-xs">
                      <Check className="h-3 w-3 mr-1" />
                      Complete
                    </Badge>
                  )}
                </div>
                <Progress value={progress} className="h-2.5" />
              </div>
            </SheetHeader>

            {/* Mobile Content */}
            <ScrollArea className="flex-1">
              <div className="space-y-4 p-4 pb-32">
                {renderSectionHeader()}
                {renderFormFields(false)}
              </div>
            </ScrollArea>

            {/* Mobile Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t p-4 z-30">
              {renderAutoSaveIndicator()}
              
              <div className="flex items-center gap-2">
                {currentSectionIndex > 0 && (
                  <Button variant="outline" size="lg" className="flex-1 h-12" onClick={handlePrevious}>
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                )}

                {(currentSectionIndex > 0 || totalSections > 1) && (
                  <Button
                    variant="outline"
                    size="lg"
                    className={cn("h-12", currentSectionIndex === 0 ? "flex-1" : "px-4")}
                    onClick={() => handleSave(false)}
                    disabled={isSaving}
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                )}

                {currentSectionIndex < totalSections - 1 ? (
                  <Button size="lg" className="flex-1 h-12 bg-gradient-to-r from-green-500 to-emerald-600" onClick={handleNext}>
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button size="lg" className="flex-1 h-12 bg-gradient-to-r from-green-500 to-emerald-600" onClick={handleSubmit} disabled={isSaving}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Submit
                  </Button>
                )}
              </div>

              <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => setShowSectionMenu(true)}>
                <Menu className="h-4 w-4 mr-2" />
                Jump to Section
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        // DESKTOP: Dialog with Sidebar
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-5xl h-[90vh] p-0 gap-0 rounded-3xl overflow-hidden">
            <div className="flex h-full">
              {/* Left Sidebar - Section Navigation */}
              <div className="w-64 border-r bg-muted/30 p-4 overflow-y-auto">
                <DialogHeader className="mb-4">
                  <DialogTitle className="text-lg">Questionnaire</DialogTitle>
                  <DialogDescription className="text-xs">
                    {loanTypeName} Application
                  </DialogDescription>
                </DialogHeader>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-2">
                    <span>Progress</span>
                    <span className="font-medium">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>

                {/* Section List */}
                <nav className="space-y-1">
                  {sections.map((section, index) => {
                    const isComplete = isSectionComplete(section, formData);
                    const isCurrent = index === currentSectionIndex;
                    const SectionIcon = getSectionIcon(section);
                    
                    return (
                      <button
                        key={section.id}
                        onClick={() => setCurrentSectionIndex(index)}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left",
                          isCurrent ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                        )}
                      >
                        <div className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                          isCurrent ? "bg-primary-foreground/20" : "bg-background"
                        )}>
                          {isComplete ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <SectionIcon className={cn("h-4 w-4", isCurrent && "text-primary-foreground")} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{section.title}</p>
                          <p className={cn(
                            "text-xs",
                            isCurrent ? "text-primary-foreground/70" : "text-muted-foreground"
                          )}>
                            {section.fields.length} field{section.fields.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Right Content - Form */}
              <div className="flex-1 flex flex-col">
                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6">
                  {/* Section Header */}
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                        {React.createElement(getSectionIcon(currentSection), { className: "h-6 w-6 text-primary" })}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">{currentSection.title}</h2>
                        {currentSection.description && (
                          <p className="text-sm text-muted-foreground">{currentSection.description}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Form Fields - 2 Column Layout */}
                  {renderFormFields(true)}
                </div>

                {/* Footer Actions */}
                <div className="border-t p-6 bg-muted/30">
                  {renderAutoSaveIndicator()}

                  <div className="flex items-center gap-3">
                    {currentSectionIndex > 0 && (
                      <Button variant="outline" onClick={handlePrevious}>
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        Previous
                      </Button>
                    )}
                    <div className="flex-1" />
                    {currentSectionIndex < totalSections - 1 ? (
                      <Button onClick={handleNext} className="bg-gradient-to-r from-green-500 to-emerald-600">
                        Next
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button onClick={handleSubmit} disabled={isSaving} className="bg-gradient-to-r from-green-500 to-emerald-600">
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Submit Application
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Mobile Section Menu Sheet */}
      <Sheet open={showSectionMenu} onOpenChange={setShowSectionMenu}>
        <SheetContent side="bottom" className="h-[60vh] rounded-t-3xl">
          <SheetHeader className="mb-4">
            <SheetTitle>Jump to Section</SheetTitle>
            <SheetDescription>Select a section to navigate quickly</SheetDescription>
          </SheetHeader>
          <ScrollArea className="h-[calc(60vh-8rem)]">
            <div className="space-y-2">
              {sections.map((section, index) => {
                const isComplete = isSectionComplete(section, formData);
                const isCurrent = index === currentSectionIndex;
                const SectionIcon = getSectionIcon(section);
                
                return (
                  <button
                    key={section.id}
                    onClick={() => {
                      setCurrentSectionIndex(index);
                      setShowSectionMenu(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all",
                      isCurrent ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                    )}
                  >
                    <div className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                      isCurrent ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}>
                      <SectionIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-medium text-sm">{section.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {section.fields.length} field{section.fields.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {isComplete && <Check className="h-5 w-5 text-green-600 shrink-0" />}
                    {isCurrent && !isComplete && <div className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
        </>
      )}
    </>
  );
}
